// Variant-specific shipping and store pickup overview.
if (!customElements.get('pickup-availability')) {
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends HTMLElement {
      constructor() {
        super();

        this.errorHtml = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);
        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        this.requestSequence = 0;

        if (this.dataset.variantId) this.fetchAvailability(this.dataset.variantId);
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.removeDrawer();
      }

      fetchAvailability(variantId) {
        if (!variantId) {
          this.renderError(false);
          return;
        }

        this.dataset.variantId = variantId;
        this.abortController?.abort();
        this.abortController = new AbortController();
        const requestSequence = ++this.requestSequence;
        const rootUrl = this.dataset.rootUrl.endsWith('/') ? this.dataset.rootUrl : `${this.dataset.rootUrl}/`;
        const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;

        fetch(variantSectionUrl, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Pickup availability request failed: ${response.status}`);
            return response.text();
          })
          .then((text) => {
            if (requestSequence !== this.requestSequence || this.dataset.variantId !== `${variantId}`) return;

            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');
            this.renderPreview(sectionInnerHTML);
          })
          .catch((error) => {
            if (error.name === 'AbortError' || requestSequence !== this.requestSequence) return;
            this.renderError();
          });
      }

      onClickRefreshList() {
        this.fetchAvailability(this.dataset.variantId);
      }

      update(variant) {
        this.abortController?.abort();
        this.requestSequence += 1;

        if (!variant?.id) {
          delete this.dataset.variantId;
          this.renderError(false);
          return;
        }

        this.fetchAvailability(variant.id);
      }

      renderError(canRetry = true) {
        this.removeDrawer();
        this.removeAttribute('available');
        this.innerHTML = '';

        if (!this.errorHtml) return;

        this.appendChild(this.errorHtml.cloneNode(true));
        const button = this.querySelector('button');
        if (!button) return;

        if (canRetry) {
          button.addEventListener('click', this.onClickRefreshList);
        } else {
          button.remove();
        }
      }

      renderPreview(sectionInnerHTML) {
        const preview = sectionInnerHTML?.querySelector('pickup-availability-preview');
        const nextDrawer = sectionInnerHTML?.querySelector('pickup-availability-drawer');

        if (sectionInnerHTML?.querySelector('[data-pickup-availability-not-applicable]')) {
          this.removeDrawer();
          this.removeAttribute('available');
          this.innerHTML = '';
          return;
        }

        if (!preview || !nextDrawer) {
          this.renderError();
          return;
        }

        const keepDrawerOpen = this.drawer?.hasAttribute('open') ?? false;
        this.removeDrawer(false);
        this.innerHTML = preview.outerHTML;
        this.setAttribute('available', '');

        this.drawer = nextDrawer;
        const colorClassesToApply = this.dataset.productPageColorScheme?.split(' ').filter(Boolean) ?? [];
        colorClassesToApply.forEach((colorClass) => this.drawer.classList.add(colorClass));
        document.body.appendChild(this.drawer);

        const button = this.querySelector('[data-pickup-availability-trigger]');
        button?.addEventListener('click', () => this.drawer?.show(button));

        if (keepDrawerOpen) this.drawer.show(button);
      }

      removeDrawer(restoreFocus = true) {
        if (!this.drawer) return;

        this.drawer.close({ restoreFocus });
        this.drawer.remove();
        this.drawer = null;
      }
    }
  );
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define(
    'pickup-availability-drawer',
    class PickupAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();

        this.onBodyClick = this.handleBodyClick.bind(this);
        this.querySelector('.pickup-availability-drawer-button')?.addEventListener('click', () => this.close());

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.close();
        });
      }

      disconnectedCallback() {
        document.body.removeEventListener('click', this.onBodyClick);
      }

      handleBodyClick(event) {
        const target = event.target;
        const isVariantSelection = target.closest('variant-selects, variant-radios');
        if (
          !target.closest('pickup-availability-drawer') &&
          !target.closest('[data-pickup-availability-trigger]') &&
          !isVariantSelection
        ) {
          this.close();
        }
      }

      close({ restoreFocus = true } = {}) {
        const wasOpen = this.hasAttribute('open');
        this.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClick);
        document.body.classList.remove('overflow-hidden');

        if (wasOpen) {
          removeTrapFocus(restoreFocus ? this.focusElement : null);
        }
      }

      hide() {
        this.close();
      }

      show(focusElement) {
        this.focusElement = focusElement;
        this.setAttribute('open', '');
        document.body.addEventListener('click', this.onBodyClick);
        document.body.classList.add('overflow-hidden');
        trapFocus(this, this.querySelector('.pickup-availability-drawer-button'));
      }
    }
  );
}
